import { useAuthUser } from 'react-auth-kit';

export type Resource = 'electricity' | 'forecasts' | 'users' | 'admin' | 'analytics' | 'api_keys' | 'plans' | 'forum' | 'articles';
export type Action = 'read' | 'write' | 'delete' | 'manage_users' | 'manage_roles' | 'manage_settings' | 'delete_users'  | 'manage' | 'debug' | 'extended' | 'pro' | 'custom_weights' | 'user_insights' | 'approve_changes' | 'moderate';

export interface UsePermissionsReturn {
  hasPermission: (resource: Resource, action?: Action) => boolean;
  hasRole: (roleName: string) => boolean;
  isAdmin: () => boolean;
  permissions: string[];
  roles: string[];
}

export const usePermissions = (): UsePermissionsReturn => {
  const auth = useAuthUser();
  const authData = auth();

  const permissions: string[] = authData?.permissions || [];
  const roles: string[] = authData?.roles || [];

  const hasPermission = (resource: Resource, action: Action = 'read'): boolean => {
    const permissionKey = `${resource}:${action}`;
    return permissions.includes(permissionKey);
  };

  const hasRole = (roleName: string): boolean => {
    return roles.includes(roleName);
  };

  const isAdmin = (): boolean => {
    return hasPermission('admin', 'read');
  };

  return {
    hasPermission,
    hasRole,
    isAdmin,
    permissions,
    roles,
  };
};

/**
 * Helper component to conditionally render based on permission
 * Usage: <RequirePermission resource="electricity">{children}</RequirePermission>
 */
export const RequirePermission: React.FC<{
  resource: Resource;
  action?: Action;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ resource, action = 'read', fallback = null, children }) => {
  const { hasPermission } = usePermissions();

  if (hasPermission(resource, action)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

/**
 * Helper component to conditionally render based on role
 * Usage: <RequireRole role="admin">{children}</RequireRole>
 */
export const RequireRole: React.FC<{
  role: string;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ role, fallback = null, children }) => {
  const { hasRole } = usePermissions();

  if (hasRole(role)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};
